if (!customElements.get('product-details')) {
class ProductDetails extends HTMLElement {
  connectedCallback() {
    this.optionPicker = this.querySelector('[data-option-picker]');
    this.form = this.querySelector('[data-product-form]');
    this.purchaseControls = this.querySelector('[data-purchase-controls]');
    this.variantIdInput = this.querySelector('[data-variant-id-input]');
    this.status = this.querySelector('[data-product-form-status]');
    this.submitButton = this.querySelector('[data-add-to-cart]');
    this.sizeSpecification = this.querySelector('[data-size-specification-output]');
    this.confirmedSizeSpecification = this.sizeSpecification?.textContent || '';
    this.abortController = null;
    this.handlePopState = this.handlePopState.bind(this);

    if (this.optionPicker) this.optionPicker.hidden = false;

    this.addEventListener('change', this.handleOptionChange.bind(this));
    if (this.form) this.form.addEventListener('submit', this.handleSubmit.bind(this));
    window.addEventListener('popstate', this.handlePopState);
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
    window.removeEventListener('popstate', this.handlePopState);
  }

  handleOptionChange(event) {
    if (!event.target.matches('[data-option-value-id]')) return;
    if (event.target.dataset.sizeSpecification && this.sizeSpecification) {
      this.sizeSpecification.textContent = event.target.dataset.sizeSpecification;
    }
    this.updateVariant(event.target);
  }

  async updateVariant(changedOption) {
    const selectedOptions = Array.from(this.querySelectorAll('[data-option-value-id]:checked'));
    const selectedOptionIds = selectedOptions.map((input) => input.dataset.optionValueId);
    const productUrl = changedOption.dataset.productUrl || this.dataset.productUrl;
    const requestUrl = new URL(productUrl, window.location.origin);
    requestUrl.searchParams.set('section_id', this.dataset.sectionId);
    requestUrl.searchParams.set('option_values', selectedOptionIds.join(','));

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    this.lockPurchasing();
    this.removeAttribute('data-variant-unresolved');
    this.setAttribute('aria-busy', 'true');
    this.status.textContent = '';
    this.status.setAttribute('role', 'status');

    try {
      const response = await fetch(requestUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.abortController.signal,
      });
      if (!response.ok) throw new Error(this.dataset.variantError);

      const responseText = await response.text();
      const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
      const replacement = responseDocument.querySelector('product-details');
      if (!replacement) throw new Error(this.dataset.variantError);

      const optionPosition = changedOption.dataset.optionPosition;
      const optionValueId = changedOption.dataset.optionValueId;
      const replacementFocusTarget = replacement.querySelector(
        `[data-option-position="${optionPosition}"][data-option-value-id="${optionValueId}"]`,
      );

      const publicUrl = new URL(replacement.dataset.currentUrl || productUrl, window.location.origin);
      if (!replacement.dataset.currentUrl && selectedOptionIds.length) {
        publicUrl.searchParams.set('option_values', selectedOptionIds.join(','));
      }

      this.replaceWith(replacement);
      window.history.pushState({}, '', publicUrl);
      window.requestAnimationFrame(() => replacementFocusTarget?.focus());
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (this.sizeSpecification) {
        this.sizeSpecification.textContent = this.confirmedSizeSpecification;
      }
      this.announce(error.message || this.dataset.variantError, true);
      this.lockPurchasing();
      this.setAttribute('data-variant-unresolved', 'true');
      this.removeAttribute('aria-busy');
    }
  }

  async handlePopState() {
    const requestUrl = new URL(window.location.href);
    requestUrl.searchParams.set('section_id', this.dataset.sectionId);

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    this.lockPurchasing();
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(requestUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.abortController.signal,
      });
      if (!response.ok) throw new Error(this.dataset.variantError);

      const responseText = await response.text();
      const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
      const replacement = responseDocument.querySelector('product-details');
      if (!replacement) throw new Error(this.dataset.variantError);
      this.replaceWith(replacement);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.announce(error.message || this.dataset.variantError, true);
      this.lockPurchasing();
      this.setAttribute('data-variant-unresolved', 'true');
      this.removeAttribute('aria-busy');
    }
  }

  async handleSubmit(event) {
    if (!this.form || !this.submitButton) return;
    if (this.purchaseIsLocked()) {
      event.preventDefault();
      this.announce(this.dataset.variantError, true);
      return;
    }
    if (event.submitter && event.submitter !== this.submitButton) return;
    event.preventDefault();
    if (this.submitButton.disabled) return;

    const defaultLabel = this.submitButton.dataset.defaultLabel;
    this.submitButton.disabled = true;
    this.submitButton.textContent = this.submitButton.dataset.loadingLabel;
    this.status.textContent = '';
    this.status.setAttribute('role', 'status');

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(this.form),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || this.status.dataset.addError);

      this.announce(this.status.dataset.addedMessage, false);
      const cartLink = this.querySelector('[data-product-cart-link]');
      if (cartLink) cartLink.hidden = false;
      this.refreshCartCount();
    } catch (error) {
      this.announce(error.message || this.status.dataset.addError, true);
    } finally {
      this.submitButton.disabled = false;
      this.submitButton.textContent = defaultLabel;
    }
  }

  lockPurchasing() {
    if (!this.purchaseControls) return;
    this.purchaseControls.disabled = true;
    this.purchaseControls.inert = true;
  }

  purchaseIsLocked() {
    return (
      this.hasAttribute('aria-busy') ||
      this.hasAttribute('data-variant-unresolved') ||
      this.purchaseControls?.disabled ||
      !this.variantIdInput?.value
    );
  }

  announce(message, isError) {
    if (!this.status) return;
    this.status.setAttribute('role', isError ? 'alert' : 'status');
    this.status.textContent = message;
    if (isError) this.status.focus();
  }

  async refreshCartCount() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach((count) => {
        count.textContent = cart.item_count;
      });
      document.querySelectorAll('[data-cart-link]').forEach((link) => {
        const itemLabel = cart.item_count === 1 ? link.dataset.cartItem : link.dataset.cartItems;
        link.setAttribute('aria-label', `${link.dataset.cartLabel}, ${cart.item_count} ${itemLabel}`);
      });
    } catch (_error) {
      // The item was added successfully; a cart-count refresh failure is non-blocking.
    }
  }
}

customElements.define('product-details', ProductDetails);
}
